/**
 * «Overganger»-fanens del av bookingadressen: søket, filtrene og perioden (Atle, 28. september).
 *
 * Samme regler som registerets filtre i `search-params.ts`: kommalister, fravær er standard, og en
 * ukjent verdi droppes framfor å bli en feil. Egne navn med `m`-prefiks, fordi et fanebytte skal
 * kunne stå med sine egne filtre uten å låne registerets «Sjanger» eller «Byrå».
 */
import { AGENCY_MOVE_KINDS, MONTH_PATTERN, type AgencyMoveKind } from '@tvitvi/shared';

export interface MoveSearch {
  mq?: string;
  mkind?: string;
  magency?: string;
  mgenre?: string;
  mfrom?: string;
  mto?: string;
  /** «Bare favoritter». Bare `true` skrives. */
  mfav?: boolean;
}

export const MOVE_SEARCH_KEYS = [
  'mq',
  'mkind',
  'magency',
  'mgenre',
  'mfrom',
  'mto',
  'mfav',
] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_VALUES = 20;

function parts(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const found = raw
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part !== '');
  return [...new Set(found)].slice(0, MAX_VALUES);
}

const isKind = (value: string): value is AgencyMoveKind =>
  (AGENCY_MOVE_KINDS as readonly string[]).includes(value);

export function validateMoveSearch(raw: Record<string, unknown>): MoveSearch {
  const search: MoveSearch = {};
  const q = raw['mq'];
  if (typeof q === 'string' && q.trim() !== '') search.mq = q.trim().slice(0, 120);
  const kind = parts(raw['mkind']).filter(isKind);
  if (kind.length > 0) search.mkind = kind.join(',');
  const agency = parts(raw['magency']).filter((value) => UUID.test(value));
  if (agency.length > 0) search.magency = agency.join(',');
  const genre = parts(raw['mgenre']).map((value) => value.slice(0, 60));
  if (genre.length > 0) search.mgenre = genre.join(',');
  for (const key of ['mfrom', 'mto'] as const) {
    const value = raw[key];
    if (typeof value === 'string' && MONTH_PATTERN.test(value)) search[key] = value;
  }
  if (raw['mfav'] === true || raw['mfav'] === 'true') search.mfav = true;
  return search;
}

export const moveValues = (value: string | undefined): string[] => parts(value);

/** Filtrene slik API-et vil ha dem. Perioden bare når den er satt. */
export function moveFilters(search: MoveSearch): Record<string, string | boolean> {
  return {
    ...(search.mq === undefined ? {} : { q: search.mq }),
    ...(search.mkind === undefined ? {} : { kind: search.mkind }),
    ...(search.magency === undefined ? {} : { agencyId: search.magency }),
    ...(search.mgenre === undefined ? {} : { genre: search.mgenre }),
    ...(search.mfav === true ? { favourites: true } : {}),
  };
}

export function isMoveFiltered(search: MoveSearch): boolean {
  return MOVE_SEARCH_KEYS.some((key) => search[key] !== undefined);
}

export function withoutMoveFilters<T extends MoveSearch>(search: T): T {
  const next = { ...search };
  for (const key of MOVE_SEARCH_KEYS) delete next[key];
  return next;
}
